/**
 * Add or edit an alias (TMMAPP-0124 item 2; TMMAPI-0160 U3). A type choice — Exact, Contains or
 * Pattern — then:
 * - **Exact or Contains:** the text field.
 * - **Pattern:** two ways in, on two tabs. **Describe it** (the default for a new alias) sends the
 *   person's words to `suggest-pattern`, which fills Contains, Exact, or Write it yourself, plus
 *   the explanation (in the person's language) and the preview. An unchanged suggestion of any
 *   kind keeps the AI pill, its confidence and the explanation (TMM-B9). Nothing is saved until
 *   Save (MM-D-45). An AI
 *   refusal is answered in place (`AiWalletPrompt`, DLG-005). **Write it yourself** is the pattern
 *   field alone.
 *
 * The live preview is the server's dry run (`…/aliases/preview`), debounced, for every type: how many
 * transactions match, up to 20 of them (bank text and date), and the lines another payee holds now
 * ("{count} of these are named {payee} now"), naming the higher payee that takes them when
 * `byPayeeId` is served. The count and the conflict sentences are a polite live region; the sample list sits
 * outside it, so a change is not read as up to 20 rows. `422 PATTERN_INVALID` shows at the field
 * per `reason` (AI's own invalid pattern at the description).
 *
 * Save creates the alias. A new pattern alias places the payee in the matching order only when that
 * payee is not in it yet (first, after a named payee, or last). An exact alias never places.
 * A pattern alias's pattern patches in place. An exact alias, or a changed type, has no patch: the new
 * alias is created and the old one removed. After a pattern is saved the dialog offers to apply it
 * to the transactions it matches (`…/apply`), or Not now; applying never moves hand-named or
 * exact-alias lines, and says so.
 */
import { useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { aiDisabledCopy, errorCopy } from '@/api/errorCopy';
import {
  aiRefusal,
  isAiWalletRefusal,
  patternInvalid,
  type AiRefusal,
  type AiWalletReason,
  type PatternInvalidReason,
} from '@/api/errors';
import {
  payeesApi,
  type AliasMatchType,
  type AliasPreview,
  type Payee,
  type PayeeAlias,
  type PayeeOrderRow,
  type PatternSuggestion,
} from '@/api/payees';
import { AiPill } from '@/components/AiPill';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { TabularText } from '@/components/TabularText';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { TOP_UP_TITLE_ID, useTopUpSwap } from '@/features/aiWallet/useTopUpSwap';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { ALIAS_KIND_KEYS, ALIAS_KINDS, kindOf, type AliasKind } from './aliasKind';

/** The server's pattern limit (TMMAPI-0160 U3). */
export const PATTERN_MAX = 255;
/** The description's limit (`PayeePatternSuggestRequest`). */
export const DESCRIPTION_MAX = 500;
/** The preview waits for a pause in typing. */
export const PREVIEW_DEBOUNCE_MS = 400;

type PatternTab = 'describe' | 'write';

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; preview: AliasPreview }
  | { status: 'failed' };

type Step = { kind: 'form' } | { kind: 'apply'; alias: PayeeAlias; count: number };

/** `422 PATTERN_INVALID`'s reason, at the field. */
function invalidCopy(t: TFunction, reason: PatternInvalidReason | null): string {
  return t(
    reason === 'TOO_LONG'
      ? 'aliasDialog.invalid.TOO_LONG'
      : reason === 'TOO_SLOW'
        ? 'aliasDialog.invalid.TOO_SLOW'
        : 'aliasDialog.invalid.SYNTAX',
  );
}

export interface AliasDialogProps {
  payee: Payee;
  /** The alias being edited; null adds one. */
  alias: PayeeAlias | null;
  onClose: () => void;
  /** After a save or an apply: the page re-reads the payee. */
  onSaved: () => Promise<void>;
  onNotify: (message: string) => void;
}

/** Mounted per opening, so it starts from the alias (or empty). */
export function AliasDialog({ payee, alias, onClose, onSaved, onNotify }: AliasDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const fieldId = useId();
  const descriptionId = useId();
  const previewId = useId();
  const initialKind: AliasKind = alias === null ? 'EXACT' : kindOf(alias.matchType);
  const initialText = alias === null ? '' : (alias.pattern ?? alias.payeeNorm);
  const [kind, setKind] = useState<AliasKind>(initialKind);
  const [text, setText] = useState(initialText);
  const [patternTab, setPatternTab] = useState<PatternTab>(alias === null ? 'describe' : 'write');
  const [description, setDescription] = useState('');
  const [suggestion, setSuggestion] = useState<PatternSuggestion | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [describeError, setDescribeError] = useState<string | null>(null);
  const [aiNotice, setAiNotice] = useState<string | null>(null);
  const [walletRefusal, setWalletRefusal] = useState<
    (AiRefusal & { reason: AiWalletReason }) | null
  >(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ key: string; state: PreviewState } | null>(null);
  const [order, setOrder] = useState<PayeeOrderRow[]>([]);
  // 'last', '0' (first), or the 1-based index after that payee. Sent only when this payee is new to the order.
  const [position, setPosition] = useState('last');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<Step>({ kind: 'form' });
  const previewTicket = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Write pattern unmounts the pressed button. Focus the field the suggestion filled, once.
  const focusSuggestion = useRef(false);
  const { swap, swapped, attachPanelHost, requestHostClose } = useTopUpSwap();

  const isPattern = kind !== 'EXACT';
  // The mark is an unchanged suggestion of any kind. The kind it fills is separate.
  const aiSuggested =
    suggestion !== null && text === suggestion.pattern && kind === kindOf(suggestion.matchType);
  const matchType: AliasMatchType =
    kind === 'EXACT' ? 'EXACT' : kind === 'CONTAINS' ? 'CONTAINS' : 'REGEX';
  const trimmed = text.trim();
  const dirty =
    step.kind === 'form' &&
    (kind !== initialKind || text !== initialText || description.trim().length > 0);
  const guard = useDialogDirtyGuard(dirty, onClose, busy || suggesting);

  // The payee order, for the place picker and the conflicts' winning payee.
  useEffect(() => {
    let cancelled = false;
    payeesApi
      .order()
      .then((rows) => {
        if (!cancelled) setOrder(rows);
      })
      .catch(() => {
        /* no place picker; a winner is named only when the order loads */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The live preview: the server's dry run, after a pause; AI's suggestion brings its own. Each
  // answer is kept with what it previewed, so a stale one is never shown (or offered) for new text.
  const previewKey = `${matchType}|${trimmed}`;
  useEffect(() => {
    const ticket = ++previewTicket.current;
    if (trimmed.length === 0 || aiSuggested) return undefined;
    const key = `${matchType}|${trimmed}`;
    const handle = window.setTimeout(() => {
      payeesApi
        .previewAlias(payee.id, { matchType, pattern: trimmed })
        .then((result) => {
          if (ticket === previewTicket.current) {
            setPreview({ key, state: { status: 'ready', preview: result } });
          }
        })
        .catch((error: unknown) => {
          if (ticket !== previewTicket.current) return;
          const invalid = patternInvalid(error);
          if (invalid !== null) {
            setFieldError(invalidCopy(t, invalid.reason));
            setPreview({ key, state: { status: 'idle' } });
          } else {
            setPreview({ key, state: { status: 'failed' } });
          }
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [payee.id, matchType, trimmed, aiSuggested, t]);

  // Save gives way to the apply step in the same dialog: move focus to that step's heading.
  useEffect(() => {
    if (step.kind !== 'apply') return;
    headingRef.current?.focus();
  }, [step]);

  // Both ways out of Describe it unmount the pressed button. Land on the filled field.
  useEffect(() => {
    if (!focusSuggestion.current) return;
    const node = document.getElementById(fieldId);
    if (node === null) return;
    focusSuggestion.current = false;
    node.focus();
  }, [fieldId, kind, patternTab, suggestion, text]);

  const shownPreview: PreviewState =
    trimmed.length === 0
      ? { status: 'idle' }
      : aiSuggested && suggestion !== null
        ? { status: 'ready', preview: suggestion }
        : preview !== null && preview.key === previewKey
          ? preview.state
          : { status: 'loading' };

  function changeText(next: string) {
    setText(next);
    setFieldError(null);
    setFormError(null);
  }

  function changeKind(next: AliasKind) {
    setKind(next);
    setFieldError(null);
    setFormError(null);
  }

  async function writePattern() {
    const words = description.trim();
    if (words.length === 0 || suggesting) return;
    setSuggesting(true);
    setDescribeError(null);
    setAiNotice(null);
    setWalletRefusal(null);
    try {
      const next = await payeesApi.suggestPattern(payee.id, words);
      setSuggestion(next);
      setText(next.pattern);
      setFieldError(null);
      setFormError(null);
      // Open on the type the suggestion is, so a Contains edit stays literal text.
      if (next.matchType === 'CONTAINS') setKind('CONTAINS');
      else if (next.matchType === 'EXACT') setKind('EXACT');
      else setPatternTab('write');
      focusSuggestion.current = true;
    } catch (error) {
      const refusal = aiRefusal(error);
      const invalid = patternInvalid(error);
      if (isAiWalletRefusal(refusal)) setWalletRefusal(refusal);
      else if (refusal !== null) setAiNotice(aiDisabledCopy(t, refusal));
      else if (invalid !== null && invalid.fromAi) setDescribeError(t('aliasDialog.aiInvalid'));
      else setDescribeError(errorCopy(t, error, 'aliasDialog.aiFailed'));
    } finally {
      setSuggesting(false);
    }
  }

  async function save() {
    if (busy) return;
    if (trimmed.length === 0) {
      setFieldError(t('aliasDialog.required'));
      document.getElementById(fieldId)?.focus();
      return;
    }
    // An edit that changed nothing writes nothing.
    if (alias !== null && kind === initialKind && trimmed === initialText.trim()) {
      onClose();
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      let saved: PayeeAlias;
      const samePatternType =
        alias !== null && alias.matchType !== 'EXACT' && alias.matchType === matchType;
      if (alias !== null && samePatternType) {
        saved = await payeesApi.patchAlias(payee.id, alias.id, trimmed);
      } else {
        // Position places the payee, and only when it is not in the order yet. A later pattern,
        // an edit, and an exact alias leave the order alone.
        const placing =
          alias === null && isPattern && !order.some((row) => row.payeeId === payee.id);
        const place = placing && position !== 'last' ? Number(position) : undefined;
        saved = await payeesApi.createAlias(payee.id, {
          matchType,
          text: trimmed,
          ...(place !== undefined ? { position: place } : {}),
        });
        // An exact alias, or a changed type, has no patch: the old one goes once the new one exists.
        // Its lines keep their names (`KEEP`): they stay on this payee, which the new alias names.
        if (alias !== null) {
          try {
            await payeesApi.removeAlias(payee.id, alias.id, 'KEEP');
          } catch {
            // The new alias exists: re-read, say so, and offer no second save.
            await onSaved();
            onNotify(t('aliasDialog.oldNotRemoved'));
            onClose();
            return;
          }
        }
      }
      await onSaved();
      // The offer names how many lines match: the preview's count, or (saved before it answered)
      // one more dry run of what was saved.
      previewTicket.current += 1;
      const count =
        saved.matchType === 'EXACT'
          ? 0
          : shownPreview.status === 'ready'
            ? shownPreview.preview.matchCount
            : await payeesApi
                .previewAlias(payee.id, { matchType: saved.matchType, pattern: trimmed })
                .then((result) => result.matchCount)
                .catch(() => 0);
      if (count > 0) {
        setStep({ kind: 'apply', alias: saved, count });
        return;
      }
      onNotify(t('aliasDialog.saved'));
      onClose();
    } catch (error) {
      const invalid = patternInvalid(error);
      if (invalid !== null) {
        setFieldError(invalidCopy(t, invalid.reason));
        document.getElementById(fieldId)?.focus();
      } else {
        setFormError(errorCopy(t, error, 'aliasDialog.saveFailed'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function apply(saved: PayeeAlias) {
    if (busy) return;
    setBusy(true);
    setFormError(null);
    try {
      const moved = await payeesApi.applyPattern(saved.id);
      await onSaved();
      onNotify(t('aliasDialog.applied', { count: moved, payee: payee.name }));
      onClose();
    } catch (error) {
      setFormError(errorCopy(t, error, 'aliasDialog.applyFailed'));
    } finally {
      setBusy(false);
    }
  }

  const payeeInOrder = order.some((row) => row.payeeId === payee.id);

  const field = (
    <TextField
      id={fieldId}
      label={t(
        kind === 'EXACT'
          ? 'aliasDialog.textExact'
          : kind === 'CONTAINS'
            ? 'aliasDialog.textContains'
            : 'aliasDialog.pattern',
      )}
      value={text}
      onChange={(event) => changeText(event.target.value)}
      error={fieldError !== null}
      helperText={
        fieldError ?? t(kind === 'PATTERN' ? 'aliasDialog.patternHelp' : 'aliasDialog.textHelp')
      }
      slotProps={{
        htmlInput: {
          maxLength: PATTERN_MAX,
          spellCheck: false,
          ...(kind === 'PATTERN' ? { 'data-pattern-field': '' } : {}),
        },
        // Bank text and patterns are identifiers (TMM-T1).
        input: { sx: { typography: 'mono' } },
      }}
      fullWidth
    />
  );

  const suggestionMark = aiSuggested && suggestion !== null && (
    <Stack spacing={0.5} data-pattern-suggestion="" sx={{ alignItems: 'flex-start', mt: 1.5 }}>
      <AiPill confidence={suggestion.confidence} />
      {suggestion.explanation.length > 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {suggestion.explanation}
        </Typography>
      )}
    </Stack>
  );

  const describe = (
    <Stack spacing={1.5}>
      {walletRefusal !== null ? (
        <AiWalletPrompt
          refusal={walletRefusal}
          swap={swap}
          onCloseHost={onClose}
          onTryAgain={() => {
            setWalletRefusal(null);
            void writePattern();
          }}
        />
      ) : (
        <>
          <TextField
            id={descriptionId}
            label={t('aliasDialog.describeLabel')}
            placeholder={t('aliasDialog.describePlaceholder')}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              setDescribeError(null);
            }}
            error={describeError !== null}
            helperText={describeError ?? t('aliasDialog.describeHelp')}
            slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX } }}
            multiline
            minRows={2}
            fullWidth
          />
          {aiNotice !== null && <Alert severity="info">{aiNotice}</Alert>}
          <Box>
            <SubmitButton
              variant="outlined"
              loading={suggesting}
              disabled={description.trim().length === 0}
              onClick={() => void writePattern()}
            >
              {t('aliasDialog.writePattern')}
            </SubmitButton>
          </Box>
        </>
      )}
      {(suggestion !== null || text.length > 0) && field}
    </Stack>
  );

  const placePicker = alias === null && isPattern && !payeeInOrder && (
    <FormControl size="small" fullWidth>
      <InputLabel id={`${fieldId}-place`}>
        {t('aliasDialog.place', { payee: payee.name })}
      </InputLabel>
      <Select
        labelId={`${fieldId}-place`}
        label={t('aliasDialog.place', { payee: payee.name })}
        value={position}
        onChange={(event) => setPosition(event.target.value)}
      >
        <MenuItem value="0">{t('aliasDialog.placeFirst')}</MenuItem>
        {order.map((row, index) => (
          <MenuItem key={row.payeeId} value={String(index + 1)}>
            {t('aliasDialog.placeAfter', { payee: row.payeeName })}
          </MenuItem>
        ))}
        <MenuItem value="last">{t('aliasDialog.placeLast')}</MenuItem>
      </Select>
    </FormControl>
  );

  const title =
    step.kind === 'apply'
      ? t('aliasDialog.applyTitle')
      : alias === null
        ? t('aliasDialog.addTitle')
        : t('aliasDialog.editTitle');

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      // While the top-up shows, Esc and the backdrop are its Cancel (DLG-005).
      confirm={
        swapped
          ? { titleId: TOP_UP_TITLE_ID, onClose: () => requestHostClose(() => undefined) }
          : null
      }
      data-alias-dialog=""
    >
      <Box sx={{ display: swapped ? 'none' : 'contents' }}>
        <DialogTitle id={titleId} ref={headingRef} tabIndex={-1}>
          {title}
        </DialogTitle>
        {step.kind === 'apply' ? (
          <>
            <DialogContent>
              <Stack spacing={1.5}>
                {formError !== null && <Alert severity="error">{formError}</Alert>}
                <Typography>
                  {t('aliasDialog.applyBody', { count: step.count, payee: payee.name })}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('aliasDialog.applyNever')}
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={busy}>
                {t('aliasDialog.notNow')}
              </Button>
              <SubmitButton loading={busy} onClick={() => void apply(step.alias)}>
                {t('aliasDialog.apply', { count: step.count })}
              </SubmitButton>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                {formError !== null && <Alert severity="error">{formError}</Alert>}
                <FormControl>
                  <FormLabel id={`${fieldId}-kind`}>{t('aliasDialog.kindLabel')}</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby={`${fieldId}-kind`}
                    value={kind}
                    onChange={(event) => changeKind(event.target.value as AliasKind)}
                  >
                    {ALIAS_KINDS.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={<Radio />}
                        label={t(ALIAS_KIND_KEYS[option])}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                {kind === 'PATTERN' ? (
                  <Box>
                    <Tabs
                      value={patternTab}
                      onChange={(_event, next: PatternTab) => setPatternTab(next)}
                      aria-label={t('aliasDialog.kind.PATTERN')}
                      sx={{ mb: 2 }}
                    >
                      <Tab
                        value="describe"
                        id={`${fieldId}-tab-describe`}
                        aria-controls={`${fieldId}-panel`}
                        label={t('aliasDialog.tabDescribe')}
                      />
                      <Tab
                        value="write"
                        id={`${fieldId}-tab-write`}
                        aria-controls={`${fieldId}-panel`}
                        label={t('aliasDialog.tabWrite')}
                      />
                    </Tabs>
                    <Box
                      role="tabpanel"
                      id={`${fieldId}-panel`}
                      aria-labelledby={`${fieldId}-tab-${patternTab}`}
                    >
                      {patternTab === 'describe' ? describe : field}
                    </Box>
                    {suggestionMark}
                  </Box>
                ) : (
                  <Stack spacing={0}>
                    {field}
                    {suggestionMark}
                  </Stack>
                )}
                {placePicker}
                <PreviewRegion
                  id={previewId}
                  state={shownPreview}
                  payeeName={payee.name}
                  order={order}
                  locale={locale}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={guard.requestClose} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <SubmitButton loading={busy} disabled={suggesting} onClick={() => void save()}>
                {t('common.save')}
              </SubmitButton>
            </DialogActions>
          </>
        )}
      </Box>
      <Box ref={attachPanelHost} sx={{ display: 'contents' }} data-top-up-host="" />
    </GuardedDialog>
  );
}

function PreviewRegion({
  id,
  state,
  payeeName,
  order,
  locale,
}: {
  id: string;
  state: PreviewState;
  payeeName: string;
  order: PayeeOrderRow[];
  locale: string;
}) {
  const { t } = useTranslation();
  const countSentence =
    state.status !== 'ready'
      ? null
      : state.preview.matchCount === 0
        ? t('aliasDialog.previewNone')
        : t('aliasDialog.previewMatches', { count: state.preview.matchCount });
  return (
    <Box id={id} data-alias-preview={state.status} sx={{ minHeight: 24 }}>
      {/* Count and conflicts only: the sample list is outside, so it is not read on every change. */}
      <Box role="status" aria-live="polite">
        {state.status === 'loading' && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('aliasDialog.previewLoading')}
          </Typography>
        )}
        {state.status === 'failed' && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('aliasDialog.previewFailed')}
          </Typography>
        )}
        {state.status === 'ready' && (
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {countSentence}
            </Typography>
            {state.preview.conflicts.map((conflict) => {
              const winner =
                conflict.byPayeeId === null
                  ? undefined
                  : order.find((row) => row.payeeId === conflict.byPayeeId);
              return (
                <Alert
                  key={`${conflict.payeeId}-${conflict.byPayeeId ?? 'x'}`}
                  severity="warning"
                  data-alias-conflict={conflict.payeeId}
                >
                  {t('aliasDialog.conflict', {
                    count: conflict.count,
                    payee: conflict.payeeName,
                  })}{' '}
                  {winner !== undefined
                    ? t('aliasDialog.conflictTakes', { payee: winner.payeeName })
                    : conflict.byPayeeId === null
                      ? t('aliasDialog.conflictKept', { payee: payeeName })
                      : null}
                </Alert>
              );
            })}
          </Stack>
        )}
      </Box>
      {state.status === 'ready' && state.preview.sample.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 1, p: 0, listStyle: 'none' }} data-preview-sample="">
          {state.preview.sample.map((row, index) => (
            <Box
              component="li"
              key={`${row.postedOn}-${index}`}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', py: 0.25 }}
            >
              <TabularText variant="body2" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                {formatCivilDate(row.postedOn, locale)}
              </TabularText>
              <Typography variant="mono" component="span" sx={{ overflowWrap: 'anywhere' }}>
                {row.payee}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
